import { useEffect, type ReactNode } from "react";

export function Spinner() {
  return <div className="spinner" role="status" aria-label="Loading" />;
}

export function PageSpinner() {
  return (
    <div className="spinner-page">
      <Spinner />
    </div>
  );
}

export function Logo({ size = 40 }: { size?: number }) {
  return (
    <img
      src="/logo.png"
      alt="ClovaForm"
      width={size}
      height={size}
      style={{ objectFit: "contain" }}
    />
  );
}

export function EmptyState({
  icon,
  title,
  children,
  phase,
  action
}: {
  icon?: ReactNode;
  title: string;
  children?: ReactNode;
  phase?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      {icon && <div className="empty-icon">{icon}</div>}
      <h3>{title}</h3>
      {children && <p className="text-sm muted" style={{ maxWidth: 420 }}>{children}</p>}
      {phase && <span className="badge badge-muted phase-tag">{phase}</span>}
      {action}
    </div>
  );
}

export function Modal({
  title,
  onClose,
  children
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-label={title}>
        <h2>{title}</h2>
        {children}
      </div>
    </div>
  );
}

export function RoleBadge({ role }: { role: string }) {
  const className =
    role === "admin" ? "badge badge-accent" : role === "trainer" ? "badge badge-warning" : "badge badge-muted";
  return <span className={className}>{role}</span>;
}

export function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    active: "badge badge-success",
    disabled: "badge badge-danger",
    pending: "badge badge-accent",
    used: "badge badge-muted",
    expired: "badge badge-warning",
    revoked: "badge badge-danger"
  };
  return <span className={map[status] ?? "badge badge-muted"}>{status}</span>;
}
